/**
 * Flexible Cards Carousel Block JavaScript
 *
 * Provides carousel functionality for the flexible cards carousel block using Accessible Slick Slider
 */

import $ from 'jQuery';
// Note: The @accessible360/accessible-slick library should be included in the main bundle
// We don't need to explicitly import it here if it's loaded globally

function initFlexibleCardsCarousel(enableDebug = false) {
  // Slick caches slide dimensions at init time. When a carousel is inside a
  // Section Nav block toggled off by default, init runs against display:none
  // and slides render at zero width. Recalc when the section becomes visible.
  document.addEventListener('apta:section-shown', function (e) {
    if (!e.target || typeof e.target.querySelectorAll !== 'function') return;
    const $carousels = $(e.target).find('.flexible-cards-carousel__carousel.slick-initialized');
    $carousels.each(function () {
      $(this).slick('setPosition');
    });
  });

  // Initialize carousel functionality when DOM is ready
  initCarousel();
  // Initialize mobile filter scroll functionality
  initMobileFilterScroll();

  /**
   * Initialize the flexible cards carousel using Slick Slider
   */
  function initCarousel() {
    if (enableDebug) console.log('Flexible Cards Carousel initialization started');
    const $carousels = $('.flexible-cards-carousel__carousel');

    if ($carousels.length === 0) {
      if (enableDebug) console.warn('No flexible cards carousel found with class .flexible-cards-carousel__carousel');
      return;
    }

    if (enableDebug) console.log('Found', $carousels.length, 'carousel containers');

    // Before initializing the carousel, add arrow and dot containers if they don't exist
    $carousels.each(function() {
      const $carousel = $(this);
      const $block = $carousel.closest('.flexible-cards-carousel');

      if (enableDebug) {
        console.log('Processing carousel:', $carousel);
        console.log('Number of slides:', $carousel.find('.flexible-cards-carousel__slide').length);
      }

      // Check if custom navigation exists
      const $navigation = $block.find('.carousel-navigation');
      const $prevArrow = $navigation.find('.arrow--prev');
      const $nextArrow = $navigation.find('.arrow--next');
      const $pagination = $navigation.find('.carousel-pagination');

      if (enableDebug) {
        console.log('Found custom navigation:', $navigation.length > 0);
        console.log('Found prev arrow:', $prevArrow.length > 0);
        console.log('Found next arrow:', $nextArrow.length > 0);
        console.log('Found pagination container:', $pagination.length > 0);
      }

      // Check if slick is available
      if (typeof $.fn.slick === 'undefined') {
        if (enableDebug) console.error('Slick library is not available!');
        return;
      }

      if (enableDebug) console.log('Slick is available, initializing carousel');

      try {
        // Check if carousel is already initialized
        if ($carousel.hasClass('slick-initialized')) {
          if (enableDebug) console.log('Carousel already initialized, destroying first');
          $carousel.slick('unslick');
        }

        // Adjust options based on slide count
        const slideCount = $carousel.find('.flexible-cards-carousel__slide').length;
        const slidesToShow = Math.min(slideCount, 3.5); // Show 3.5 cards

        if (enableDebug) console.log(`Setting slidesToShow to ${slidesToShow} based on ${slideCount} slides`);

        // First, make sure slides are visible before initialization
        $carousel.find('.flexible-cards-carousel__slide').css('display', 'block');

        // Force a repaint to ensure elements are visible
        $carousel[0].offsetHeight;

        if (enableDebug) console.log('Preparing to initialize Slick with', slidesToShow, 'slides visible');

        // Initialize Slick with the specified options
        $carousel.slick({
          slidesToShow: 3,
          slidesToScroll: 1, // Scroll by 1 slide for smoother pagination
          arrows: true,
          dots: true,
          infinite: false,
          autoplay: false,
          speed: 500,
          adaptiveHeight: false,
          cssEase: 'ease-in-out',
          mobileFirst: false,
          centerMode: false,
          variableWidth: false,
          rtl: false, // Ensure left-to-right direction
          initialSlide: 0, // Start from the first slide
          dotsClass: 'carousel-pagination', // Use our custom class for dots
          prevArrow: $prevArrow, // Use the custom previous arrow
          nextArrow: $nextArrow, // Use the custom next arrow
          appendDots: $pagination, // Use the custom pagination container
          centerPadding: '0px', // No center padding to ensure proper alignment
          edgeFriction: 0.15, // Smooth edge behavior
          customPaging: function(slider, i) {
            // Create pagination dots that represent pages, not individual slides
            return '<button class="carousel-pagination__page" aria-label="Go to slide group ' + (i + 1) + '"></button>';
          },
          responsive: [
						{
              breakpoint: 1500,
              settings: {
                slidesToShow: 3.2,
                slidesToScroll: 1
              }
            },
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2.2,
                slidesToScroll: 1
              }
            },
            {
              breakpoint: 650,
              settings: {
                slidesToShow: 1.1,
                slidesToScroll: 1
              }
            }
          ]
        });

        if (enableDebug) console.log('Slider initialized successfully');

        // Add the carousel to the global scope for debugging
        if (!window.slickCarousels) window.slickCarousels = [];
        window.slickCarousels.push($carousel);

        // Check if the slick-initialized class was added
        setTimeout(() => {
          if ($carousel.hasClass('slick-initialized')) {
            if (enableDebug) console.log('Slick initialization confirmed via class');

            // Set up the active class for the first pagination button
            $pagination.find('button').first().addClass('is-active');

            // Handle custom pagination active states
            $carousel.on('beforeChange', function(event, slick, currentSlide, nextSlide) {
              // Calculate which pagination dot should be active based on the current slide group
              const slidesToScroll = slick.options.slidesToScroll;
              const activeDotIndex = Math.floor(nextSlide / slidesToScroll);

              $pagination.find('button').removeClass('is-active');
              $pagination.find('button').eq(activeDotIndex).addClass('is-active');
            });

            // Handle arrow disabled state - don't disable for infinite carousel
            $carousel.on('afterChange', function(event, slick, currentSlide) {
              // For infinite carousel, arrows should never be disabled
              if (!slick.options.infinite) {
                if (currentSlide === 0) {
                  $prevArrow.prop('disabled', true);
                } else {
                  $prevArrow.prop('disabled', false);
                }

                if (currentSlide >= slick.slideCount - slick.options.slidesToShow) {
                  $nextArrow.prop('disabled', true);
                } else {
                  $nextArrow.prop('disabled', false);
                }
              } else {
                // For infinite carousel, ensure arrows are always enabled
                $prevArrow.prop('disabled', false);
                $nextArrow.prop('disabled', false);
              }
            });
          } else {
            if (enableDebug) console.warn('Slick initialization may have failed - missing slick-initialized class');
          }

          // Log the actual DOM elements to help diagnose visibility issues
          if (enableDebug) {
            console.log('Carousel container:', $carousel[0]);
            console.log('Carousel track:', $carousel.find('.slick-track')[0]);
            console.log('Carousel slides:', $carousel.find('.slick-slide').length);
          }
        }, 500);
      } catch (error) {
        if (enableDebug) console.error('Error initializing slick slider:', error);
      }
    });
  }

  /**
   * Initialize mobile filter scrolling functionality
   * Similar to tabbed-highlighted-content pattern
   */
  function initMobileFilterScroll() {
    const $filterLists = $('.flexible-cards-carousel__filter-list');

    if ($filterLists.length === 0) return;

    $filterLists.each(function() {
      const $filterList = $(this);

      // Add grabbable class for touch scrolling on mobile
      if (window.innerWidth <= 768) { // Mobile breakpoint
        $filterList.addClass('--grabbable');

        // Add drag scrolling functionality
        let isDown = false;
        let startX;
        let scrollLeft;

        $filterList.on('mousedown touchstart', function(e) {
          isDown = true;
          const clientX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
          startX = clientX - $filterList.offset().left;
          scrollLeft = $filterList.scrollLeft();
          $filterList.css('cursor', 'grabbing');
        });

        $filterList.on('mouseleave mouseup touchend', function() {
          isDown = false;
          $filterList.css('cursor', 'grab');
        });

        $filterList.on('mousemove touchmove', function(e) {
          if (!isDown) return;
          e.preventDefault();
          const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
          const x = clientX - $filterList.offset().left;
          const walk = (x - startX) * 2; // Adjust scroll speed
          $filterList.scrollLeft(scrollLeft - walk);
        });
      }
    });

    // Re-initialize on window resize
    $(window).on('resize', function() {
      // Debounce resize handler
      clearTimeout(window.resizeTimeout);
      window.resizeTimeout = setTimeout(function() {
        initMobileFilterScroll();
      }, 100);
    });
  }
}

// Export as default to match the import pattern in main.js
export default initFlexibleCardsCarousel;
